import { logAPI, logError, logCache, devLog } from './logger';

class ApiDataLoader {
  constructor() {
    // Detectar si estamos en app nativa (Capacitor)
    const isNative = window.Capacitor?.isNativePlatform?.() || false;
    
    // URL base:
    // - En web (PWA): '/api' usa proxy de Vercel
    // - En app nativa: URL directa del servidor
    let rawBase;
    if (isNative) {
      rawBase = 'https://api.appagustinos.es/api';  // Directo puerto 3002
      logAPI('🔧 Modo nativo detectado - Usando servidor directo');
    } else {
      rawBase = process.env.REACT_APP_API_URL || '/api';
      logAPI('🌐 Modo web detectado - Usando proxy Vercel');
    }
    
    this.baseUrl = String(rawBase).replace(/\/+$/, '') || '/api';
    logAPI('API URL configurada:', this.baseUrl);
    
    // Cache mejorado con soporte para paginación
    this.cache = {
      articles: null,
      stats: null,
      lastUpdate: null,
      // Nuevo: Cache por páginas y secciones
      paginatedArticles: new Map(), // Clave: "seccion:page:limit"
      totalCounts: new Map(),       // Clave: "seccion" -> total count
      searchCache: new Map()        // Clave: "query:section:limit"
    };
    
    // Configuración de paginación
    this.paginationConfig = {
      defaultLimit: 400, // Aumentado para mejor representación por sección (~25 artículos por sección)
      maxLimit: 1000,
      supportsPagination: false // Se detecta automáticamente
    };
    
    // Detectar capacidades del backend al inicializar (no bloqueante)
    this.detectBackendCapabilities().catch(err => {
      logAPI('No se pudieron detectar capacidades del backend:', err.message);
    });
  }

  // Método principal para obtener estadísticas
  async obtenerEstadisticas() {
    try {
      logAPI('Obteniendo estadísticas...');
      
      const response = await fetch(`${this.baseUrl}/articles/stats`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      devLog('Estadísticas obtenidas:', result);
      
      // Extraer datos del formato {success: true, data: {...}}
      const stats = result.success && result.data ? result.data : result;
      
      this.cache.stats = stats;
      this.cache.lastUpdate = new Date();
      
      return stats;
      
    } catch (error) {
      console.error('❌ Error obteniendo estadísticas:', error);
      throw error;
    }
  }

  // Método para detectar capacidades del backend
  async detectBackendCapabilities() {
    try {
      logAPI('Detectando capacidades del backend...');
      
      // Timeout para la detección
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout en detección')), 3000);
      });
      
      const fetchPromise = fetch(`${this.baseUrl}/articles/sections?limit=1&page=1`);
      
      const testResponse = await Promise.race([fetchPromise, timeoutPromise]);
      
      if (testResponse.ok) {
        const testData = await testResponse.json();
        if (testData.success && testData.data && testData.data.pagination) {
          this.paginationConfig.supportsPagination = true;
          logAPI('Backend soporta paginación');
          return;
        }
      }
      
      logAPI('Backend usa API legacy (sin paginación)');
      
    } catch (error) {
      logAPI('Backend usa API legacy (sin paginación) - Error:', error.message);
    }
  }

  // Método mejorado para obtener artículos con soporte de paginación
  async agruparPorSecciones(options = {}) {
    const {
      limit = this.paginationConfig.defaultLimit,
      page = 1,
      section = null,
      forceRefresh = false
    } = options;

    // Clave de cache
    const cacheKey = `${section || 'all'}:${page}:${limit}`;
    
    // Verificar cache si no es refresh forzado
    if (!forceRefresh && this.cache.paginatedArticles.has(cacheKey)) {
      logCache(`Usando datos cacheados para ${cacheKey}`);
      return this.cache.paginatedArticles.get(cacheKey);
    }

    // Usar método con paginación si está disponible, sino fallback al original
    if (this.paginationConfig.supportsPagination) {
      return await this._agruparConPaginacion(options);
    } else {
      return await this._agruparLegacy();
    }
  }

  // Método con paginación (para cuando el backend lo soporte)
  async _agruparConPaginacion(options) {
    const { limit, page, section } = options;
    
    try {
      logAPI(`Cargando artículos con paginación (página ${page}, límite ${limit})...`);
      
      let url = `${this.baseUrl}/articles/sections?limit=${limit}&page=${page}`;
      if (section) {
        url += `&section=${encodeURIComponent(section)}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      devLog('Respuesta paginada del servidor:', result);
      
      // Extraer datos y metadatos de paginación
      let secciones, pagination;
      if (result.success && result.data) {
        secciones = result.data.articles || result.data;
        pagination = result.data.pagination;
      } else {
        throw new Error('Formato de respuesta no válido para paginación');
      }
      
      const cacheKey = `${section || 'all'}:${page}:${limit}`;
      const resultData = {
        sections: secciones,
        pagination: pagination,
        totalArticles: pagination?.total_articles || 0,
        currentPage: page,
        hasMore: pagination?.has_next || false
      };
      
      // Guardar en cache
      this.cache.paginatedArticles.set(cacheKey, resultData);
      
      logAPI(`Cargados ${Object.values(secciones).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0)} artículos (página ${page})`);
      
      return resultData;
      
    } catch (error) {
      console.error('❌ Error cargando artículos con paginación:', error);
      throw error;
    }
  }

// Método legacy (API actual)
async _agruparLegacy() {
  try {
    console.log('🔄 Cargando artículos por secciones (modo legacy)...');
    
    const response = await fetch(`${this.baseUrl}/articles/sections`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('📋 Respuesta del servidor (legacy):', result);
    
    // Extraer secciones del formato {success: true, data: {seccion: [artículos]}}
    let secciones;
    if (result.success && result.data) {
      secciones = result.data;
      console.log('✅ Secciones extraídas de result.data');
    } else if (typeof result === 'object' && !Array.isArray(result)) {
      secciones = result;
      console.log('✅ Usando result directamente como secciones');
    } else {
      console.error('❌ Formato de respuesta no reconocido:', result);
      throw new Error('Formato de respuesta del servidor no válido');
    }
    
    // Calcular total de artículos
    const totalArticles = Object.values(secciones).reduce((total, articles) => {
      return total + (Array.isArray(articles) ? articles.length : 0);
    }, 0);
    
    console.log(`✅ Cargados ${totalArticles} artículos (limitado a 100 por backend)`);
    
    // Log de distribución
    console.log('📊 Distribución por secciones:');
    Object.keys(secciones).forEach(seccion => {
      const articulos = secciones[seccion];
      if (Array.isArray(articulos)) {
        console.log(`  📁 ${seccion}: ${articulos.length} artículos`);
        // Guardar conteo en cache
        this.cache.totalCounts.set(seccion, articulos.length);
      } else {
        console.log(`  ⚠️ ${seccion}: formato no válido`);
      }
    });
    
    // Cache legacy
    this.cache.articles = secciones;
    this.cache.lastUpdate = new Date();
    
    // También cachear como resultado paginado para compatibilidad
    const legacyResult = {
      sections: secciones,
      pagination: {
        current_page: 1,
        total_pages: 1,
        total_articles: totalArticles,
        articles_per_page: totalArticles,
        has_next: false,
        has_previous: false,
        is_limited: true // Indicador de que está limitado
      },
      totalArticles: totalArticles,
      currentPage: 1,
      hasMore: false,
      source: 'general', // ← NUEVA ETIQUETA
      isLimited: true    // ← NUEVA ETIQUETA
    };
    
    this.cache.paginatedArticles.set('all:1:' + totalArticles, legacyResult);
    
    console.log('🎉 Datos legacy cargados exitosamente!');
    
    return legacyResult;
    
  } catch (error) {
    console.error('❌ Error cargando artículos:', error);
    throw error;
  }
}

// MÉTODO PARA CARGAR SECCIÓN COMPLETA (lazy loading)
async cargarSeccionCompleta(nombreSeccion, limite = 500) {
  try {
    logAPI(`Cargando sección completa: "${nombreSeccion}"`);
    
    const response = await fetch(`${this.baseUrl}/articles/sections?section=${encodeURIComponent(nombreSeccion)}&limit=${limite}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    const articulos = result.data?.articles?.[nombreSeccion] || [];
    
    logAPI(`✅ Cargados ${articulos.length} artículos de "${nombreSeccion}" (carga específica)`);
    
    // Actualizar cache con datos completos de la sección
    const cacheKey = `section-${nombreSeccion}`;
    this.cache[cacheKey] = {
      data: articulos,
      timestamp: Date.now(),
      section: nombreSeccion,
      source: 'specific', // ← NUEVA ETIQUETA
      isComplete: true    // ← NUEVA ETIQUETA
    };
    
    return articulos;
    
  } catch (error) {
    logError(`Error cargando sección "${nombreSeccion}":`, error);
    return [];
  }
}

  // Método para obtener un artículo específico
  async obtenerArticulo(id) {
    try {
      console.log(`🔄 Obteniendo artículo ${id}...`);
      
      const response = await fetch(`${this.baseUrl}/articles/${id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Extraer artículo del formato {success: true, data: {...}}
      const article = result.success && result.data ? result.data : result;
      
      console.log(`✅ Artículo ${id} obtenido:`, article.title);
      
      return article;
      
    } catch (error) {
      console.error(`❌ Error obteniendo artículo ${id}:`, error);
      throw error;
    }
  }

  // Nuevo: Método para buscar artículos
  async buscarArticulos(query, options = {}) {
    const {
      section = null,
      limit = this.paginationConfig.defaultLimit,
      page = 1
    } = options;

    const cacheKey = `${query}:${section || 'all'}:${limit}:${page}`;
    
    // Verificar cache
    if (this.cache.searchCache.has(cacheKey)) {
      console.log(`🔍 Usando búsqueda cacheada para "${query}"`);
      return this.cache.searchCache.get(cacheKey);
    }

    try {
      if (this.paginationConfig.supportsPagination) {
        // Búsqueda con API mejorada
        let url = `${this.baseUrl}/articles/search?q=${encodeURIComponent(query)}&limit=${limit}&page=${page}`;
        if (section) {
          url += `&section=${encodeURIComponent(section)}`;
        }
        
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        
        const result = await response.json();
        const searchResults = {
          articles: result.data.articles || result.data,
          pagination: result.data.pagination,
          query: query,
          totalResults: result.data.pagination?.total_articles || 0
        };
        
        this.cache.searchCache.set(cacheKey, searchResults);
        return searchResults;
        
      } else {
        // Búsqueda local en datos cacheados (fallback)
        console.log(`🔍 Búsqueda local para "${query}"`);
        return this._busquedaLocal(query, options);
      }
      
    } catch (error) {
      console.error(`❌ Error en búsqueda para "${query}":`, error);
      // Fallback a búsqueda local
      return this._busquedaLocal(query, options);
    }
  }

  // Búsqueda local en datos cacheados
  _busquedaLocal(query, options = {}) {
    const { section = null, limit = this.paginationConfig.defaultLimit } = options;
    
    if (!this.cache.articles) {
      console.warn('⚠️ No hay datos cacheados para búsqueda local');
      return { articles: {}, totalResults: 0, query: query, isLocal: true };
    }

    const todosLosArticulos = section 
      ? (this.cache.articles[section] || [])
      : Object.values(this.cache.articles).flat();
    
    const resultados = todosLosArticulos.filter(articulo => 
      articulo.title?.toLowerCase().includes(query.toLowerCase()) ||
      (articulo.description || '').toLowerCase().includes(query.toLowerCase()) ||
      (articulo.subtitle || '').toLowerCase().includes(query.toLowerCase()) ||
      (articulo.author || '').toLowerCase().includes(query.toLowerCase())
    ).slice(0, limit);
    
    console.log(`🔍 Búsqueda local: ${resultados.length} resultados para "${query}"`);
    
    return {
      articles: section ? { [section]: resultados } : this._agruparResultadosPorSeccion(resultados),
      totalResults: resultados.length,
      query: query,
      isLocal: true,
      isLimited: resultados.length === limit
    };
  }

  // Agrupar resultados de búsqueda por sección
  _agruparResultadosPorSeccion(articulos) {
    const agrupados = {};
    articulos.forEach(articulo => {
      const seccion = articulo.section || 'Sin categoría';
      if (!agrupados[seccion]) agrupados[seccion] = [];
      agrupados[seccion].push(articulo);
    });
    return agrupados;
  }

  // Nuevo: Cargar más artículos de una sección específica
  async cargarMasArticulos(seccion, options = {}) {
    const { limit = this.paginationConfig.defaultLimit } = options;
    
    if (!this.paginationConfig.supportsPagination) {
      console.warn(`⚠️ No se pueden cargar más artículos de "${seccion}" - API legacy limitada a 100 total`);
      return this.cache.articles?.[seccion] || [];
    }

    // Determinar qué página cargar basándose en cache existente
    let nextPage = 1;
    const existingPages = Array.from(this.cache.paginatedArticles.keys())
      .filter(key => key.startsWith(`${seccion}:`))
      .map(key => parseInt(key.split(':')[1]))
      .sort((a, b) => b - a);
    
    if (existingPages.length > 0) {
      nextPage = existingPages[0] + 1;
    }

    console.log(`📚 Cargando página ${nextPage} de "${seccion}"...`);
    
    try {
      const result = await this.agruparPorSecciones({
        section: seccion,
        page: nextPage,
        limit: limit
      });
      
      return result.sections[seccion] || [];
      
    } catch (error) {
      console.error(`❌ Error cargando más artículos de "${seccion}":`, error);
      throw error;
    }
  }

  // Método combinado mejorado para inicializar toda la app
  async inicializar(options = {}) {
    const { loadAll = false, forceRefresh = false } = options;
    
    try {
      logAPI('Inicializando ApiDataLoader mejorado...');
      
      // Cargar estadísticas y artículos en paralelo
      const [stats, sectionsResult] = await Promise.all([
        this.obtenerEstadisticas(),
        this.agruparPorSecciones(loadAll ? { limit: 1200, forceRefresh } : { limit: 200, page: 1, forceRefresh })
      ]);
      
      logAPI('Inicialización completa');
      
      // Información mejorada sobre capacidades
      const capabilities = {
        supportsPagination: this.paginationConfig.supportsPagination,
        supportsSearch: this.paginationConfig.supportsPagination,
        isLimited: !this.paginationConfig.supportsPagination || sectionsResult.pagination?.is_limited,
        maxArticlesShown: sectionsResult.totalArticles,
        totalArticlesAvailable: stats.total_articles || sectionsResult.totalArticles
      };
      
      return {
        stats,
        sections: sectionsResult.sections,
        pagination: sectionsResult.pagination,
        totalArticles: sectionsResult.totalArticles,
        totalSections: Object.keys(sectionsResult.sections).length,
        capabilities,
        hasMore: sectionsResult.hasMore,
        source: sectionsResult.source || 'general',       // ← AGREGAR
        isLimited: sectionsResult.isLimited !== false     // ← AGREGAR
      };
      
    } catch (error) {
      console.error('❌ Error en inicialización:', error);
      throw error;
    }
  }

  // Método para verificar la conexión
  async verificarConexion() {
    try {
      logAPI('Verificando conexión con servidor...');
      
      const response = await fetch(`${this.baseUrl}/articles/stats`);
      
      if (!response.ok) {
        throw new Error(`Health check failed: ${response.status}`);
      }
      
      const health = await response.json();
      logAPI('Conexión verificada:', health);
      
      return health;
      
    } catch (error) {
      console.error('❌ Error en verificación de conexión:', error);
      throw error;
    }
  }

  // Getter mejorado para obtener datos cacheados
  get datosCacheados() {
    return {
      articles: this.cache.articles,
      stats: this.cache.stats,
      lastUpdate: this.cache.lastUpdate,
      paginatedArticles: Object.fromEntries(this.cache.paginatedArticles),
      totalCounts: Object.fromEntries(this.cache.totalCounts),
      searchCache: Object.fromEntries(this.cache.searchCache),
      capabilities: {
        supportsPagination: this.paginationConfig.supportsPagination,
        defaultLimit: this.paginationConfig.defaultLimit
      }
    };
  }

  // Nuevo: Obtener información de paginación para una sección
  getPaginationInfo(seccion = null) {
    const key = `${seccion || 'all'}:1:${this.paginationConfig.defaultLimit}`;
    const cachedData = this.cache.paginatedArticles.get(key);
    
    if (cachedData && cachedData.pagination) {
      return cachedData.pagination;
    }
    
    // Fallback para API legacy
    const totalCount = seccion 
      ? (this.cache.totalCounts.get(seccion) || 0)
      : Array.from(this.cache.totalCounts.values()).reduce((sum, count) => sum + count, 0);
      
    return {
      current_page: 1,
      total_pages: 1,
      total_articles: totalCount,
      articles_per_page: totalCount,
      has_next: false,
      has_previous: false,
      is_limited: true
    };
  }

  // Método mejorado para limpiar caché
  limpiarCache(tipo = 'all') {
    switch (tipo) {
      case 'articles':
        this.cache.articles = null;
        this.cache.paginatedArticles.clear();
        console.log('🧹 Caché de artículos limpiado');
        break;
      case 'search':
        this.cache.searchCache.clear();
        console.log('🧹 Caché de búsquedas limpiado');
        break;
      case 'stats':
        this.cache.stats = null;
        console.log('🧹 Caché de estadísticas limpiado');
        break;
      case 'all':
      default:
        this.cache = {
          articles: null,
          stats: null,
          lastUpdate: null,
          paginatedArticles: new Map(),
          totalCounts: new Map(),
          searchCache: new Map()
        };
        console.log('🧹 Todo el caché limpiado');
        break;
    }
  }
}

// Crear instancia global con manejo de errores
try {
  logAPI('Creando instancia de ApiDataLoader...');
  window.agustinosDataLoader = new ApiDataLoader();
  logAPI('window.agustinosDataLoader creado exitosamente');
} catch (error) {
  logError('Error creando ApiDataLoader:', error);
  // Crear una instancia básica como fallback
  window.agustinosDataLoader = {
    error: true,
    message: 'Error en inicialización: ' + error.message
  };
}

// Debug mejorado en consola
window.debugAgustinos = {
  // Métodos existentes
  verificarConexion: () => window.agustinosDataLoader.verificarConexion(),
  obtenerStats: () => window.agustinosDataLoader.obtenerEstadisticas(),
  obtenerSecciones: (options) => window.agustinosDataLoader.agruparPorSecciones(options),
  inicializar: (options) => window.agustinosDataLoader.inicializar(options),
  cache: () => window.agustinosDataLoader.datosCacheados,
  limpiarCache: (tipo) => window.agustinosDataLoader.limpiarCache(tipo),
  
  // Nuevos métodos de debug
  buscar: (query, options) => window.agustinosDataLoader.buscarArticulos(query, options),
  cargarMas: (seccion, options) => window.agustinosDataLoader.cargarMasArticulos(seccion, options),
  paginacion: (seccion) => window.agustinosDataLoader.getPaginationInfo(seccion),
  capacidades: () => ({
    supportsPagination: window.agustinosDataLoader.paginationConfig.supportsPagination,
    defaultLimit: window.agustinosDataLoader.paginationConfig.defaultLimit,
    cacheSize: {
      articles: window.agustinosDataLoader.cache.paginatedArticles.size,
      searches: window.agustinosDataLoader.cache.searchCache.size,
      sections: window.agustinosDataLoader.cache.totalCounts.size
    }
  }),
  
  // Helpers para testing
  simularPaginacion: () => {
    window.agustinosDataLoader.paginationConfig.supportsPagination = true;
    console.log('✅ Paginación simulada activada para testing');
  },
  resetearPaginacion: () => {
    window.agustinosDataLoader.paginationConfig.supportsPagination = false;
    console.log('↩️ Paginación reseteada a detección automática');
  }
};

logAPI('ApiDataLoader mejorado inicializado correctamente');
devLog('Debug disponible en window.debugAgustinos');
devLog('Nuevas funciones: buscar(), cargarMas(), paginacion(), capacidades()');
devLog('Configuración: simularPaginacion(), resetearPaginacion()');

export default ApiDataLoader;